import { Table, Typography, Tooltip, Button, Empty, Flex, theme } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { EnrichedFlowExecution } from '@/api/logs';
import { CopyableId } from '@/components/conversation-overview';
import { ExecutionStatusTag } from './ExecutionStatusTag';
import { formatDuration, formatWhen } from '@/utils/dateFormat';
import { useResponsivePinnedColumn } from '@/hooks/useResponsivePinnedColumn';

const { Text } = Typography;

export interface FlowExecutionsTableProps {
  executions: EnrichedFlowExecution[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  /** Shows the tenant column (global page); hidden when the page is already scoped to one tenant */
  showTenant: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onOpen: (execution: EnrichedFlowExecution) => void;
}

/**
 * Server-paginated list of flow executions: when, status, tenant, flow, duration and error.
 */
export function FlowExecutionsTable({
  executions,
  total,
  page,
  pageSize,
  loading,
  showTenant,
  onPageChange,
  onOpen
}: FlowExecutionsTableProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();

  const pinColumn = useResponsivePinnedColumn();
  const columns: ColumnsType<EnrichedFlowExecution> = [
    {
      title: t('logs.executions.columns.time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (value: string) => <Text style={{ whiteSpace: 'nowrap' }}>{formatWhen(value)}</Text>
    },
    {
      title: t('logs.executions.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => <ExecutionStatusTag status={status} />
    },
    ...(showTenant
      ? [
          {
            title: t('logs.executions.columns.tenant'),
            key: 'tenant',
            width: 180,
            render: (_: unknown, row: EnrichedFlowExecution) => row.tenant?.client.name ?? <Text type="secondary">—</Text>
          }
        ]
      : []),
    {
      title: t('logs.executions.columns.flow'),
      key: 'flow',
      width: 240,
      render: (_, row) =>
        row.clientFlow ? (
          <div>
            <Text>{row.clientFlow.name}</Text>
            {row.clientFlow.masterFlow && (
              <div>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {row.clientFlow.masterFlow.displayName} · v{row.clientFlow.masterFlow.version}
                </Text>
              </div>
            )}
          </div>
        ) : (
          <Text type="secondary">{row.clientFlowId}</Text>
        )
    },
    {
      title: t('logs.executions.columns.duration'),
      dataIndex: 'durationMs',
      key: 'durationMs',
      width: 110,
      render: (value: number | null | undefined) => formatDuration(value, 'precise')
    },
    {
      title: t('logs.executions.columns.error'),
      key: 'error',
      width: 260,
      render: (_, row) =>
        row.errorMessage ? (
          <Tooltip title={row.errorThrownInNode ? t('logs.executions.failedInNode', { node: row.errorThrownInNode }) : undefined}>
            <Text type="danger" ellipsis style={{ maxWidth: 240 }}>
              {row.errorMessage}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        )
    },
    {
      title: t('logs.executions.columns.correlationId'),
      dataIndex: 'correlationId',
      key: 'correlationId',
      width: 200,
      render: (value: string | null | undefined) => (value ? <CopyableId value={value} /> : <Text type="secondary">—</Text>)
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      fixed: pinColumn('right'),
      render: (_, row) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onOpen(row)}>
          {t('logs.view')}
        </Button>
      )
    }
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={executions}
      loading={loading}
      scroll={{ x: 'max-content' }}
      onRow={(row) => ({ onDoubleClick: () => onOpen(row) })}
      pagination={{
        current: page,
        pageSize,
        total,
        showTotal: (count, range) => t('logs.pagination', { start: range[0], end: range[1], total: count }),
        onChange: onPageChange,
        style: { paddingInline: token.padding }
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical align="center" gap={token.marginXXS}>
                <Text>{t('logs.executions.empty')}</Text>
                <Text type="secondary">{t('logs.emptyHint')}</Text>
              </Flex>
            }
          />
        )
      }}
    />
  );
}
